import React from 'react';
import axios from 'axios';
import { objectToCamel } from 'ts-case-convert';

import { getBaseUrl } from '../utils/baseUrl';
import { Settings, PaymentMethodsConfig } from '../@types';
import { billingConfigApiToObject } from '../utils/billing';

const DefaultErrorMessage = 'Failed to retrieve property configuration.';

type SettingsReturnType = [Settings | null, boolean, string | null];

const baseUrl = getBaseUrl();
const apiEndpoint = '/payment/PaymentElement/v2/settings';

export function useSettings(token: string, propertyId: number): SettingsReturnType {
  const [data, setData] = React.useState<Settings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data: response } = await axios.post(apiEndpoint, {
          property_id: propertyId,
          origin: window.location.origin,
        }, {
          baseURL: baseUrl,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.success) throw new Error(response.statusMessage || DefaultErrorMessage);

        const settings = {
          billingAddressFields: billingConfigApiToObject(response.data.billing_address_fields),
          mode: response.data.mode,
          paymentMethods: response.data.payment_methods,
          paymentMethodConfigurations: Object.keys(response.data.payment_method_configurations).reduce(
            (next, key) => ({
              ...next,
              [key]: objectToCamel(response.data.payment_method_configurations[key]),
            }),
            {} as PaymentMethodsConfig,
          ),
          processors: objectToCamel(response.data.processors),
        } as Settings;

        setData(settings);
      } catch (err) {
        setError((err as any).message || DefaultErrorMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, propertyId]);

  return [data, loading, error];
}
